import type {
    ActivityLogEntryDto,
    ActivityLogResponseDto,
    PenOverviewItemDto,
    PenOverviewResponseDto,
    PenSummaryDto,
    TaskListResponseDto,
    TaskResponseDto,
} from '@/services/types';

import type {
    ActivityLog,
    ActivityLogEntry,
    ActivityLogPagination,
    BioscopeStatus,
    PenOverview,
    PenOverviewItem,
    PenStatus,
    PenSummary,
    Task,
    TaskCategory,
    TaskList,
    TaskListSummary,
    TaskPriority,
    TaskStatus,
    TaskTemplateVariables,
} from './types';

// ── Helpers ─────────────────────────────────────────────────────────────────

const toDateOrNull = (value: string | null | undefined): Date | null =>
    value ? new Date(value) : null;

/** Narrows an API string to one of a union's members, or null when it is none of them. */
function oneOf<T extends string>(members: readonly T[], value: unknown): T | null {
    return members.find((member) => member === value) ?? null;
}

const TASK_STATUSES = ['active', 'completed', 'ignored'] as const;
const TASK_PRIORITIES = ['critical', 'high', 'warning', 'low'] as const;
const TASK_CATEGORIES = [
    'connectivity',
    'placement',
    'image_quality',
    'equipment',
    'data_quality',
] as const;
const BIOSCOPE_STATUSES = ['measuring', 'offline'] as const;
const PEN_STATUSES = ['all_good', 'warning', 'critical', 'offline'] as const;

function toTemplateVariables(value: unknown): TaskTemplateVariables | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? { ...value }
        : null;
}

// ── Task Mappers ────────────────────────────────────────────────────────────

/**
 * The backend's unions match the domain ones. A value outside them (a backend ahead of this
 * build) reads as an active, low-priority task rather than an unknown that breaks the badges.
 */
export const mapTaskToDomain = (dto: TaskResponseDto): Task => ({
    id: dto.id,
    alertId: dto.alertId ?? null,
    locationId: dto.locationId,
    cageId: dto.cageId ?? null,
    cageName: dto.cageName ?? null,
    bioscopeId: dto.bioscopeId ?? null,
    alertCode: dto.alertCode ?? null,
    type: dto.type,
    status: oneOf<TaskStatus>(TASK_STATUSES, dto.status) ?? 'active',
    priority: oneOf<TaskPriority>(TASK_PRIORITIES, dto.priority) ?? 'low',
    category: oneOf<TaskCategory>(TASK_CATEGORIES, dto.category),
    completedAt: toDateOrNull(dto.completedAt),
    autoResolved: dto.autoResolved ?? false,
    ignoredUntil: toDateOrNull(dto.ignoredUntil),
    ignoredPermanently: dto.ignoredPermanently ?? false,
    createdAt: new Date(dto.createdAt),
    recurrenceCount: dto.recurrenceCount ?? 0,
    templateVariables: toTemplateVariables(dto.templateVariables) ?? {},
});

export const mapTaskSummaryToDomain = (
    dto: Partial<TaskListResponseDto['summary']>
): TaskListSummary => ({
    total: dto.total ?? 0,
    active: dto.active ?? 0,
    completed: dto.completed ?? 0,
    ignored: dto.ignored ?? 0,
});

export const mapTaskListToDomain = (dto: TaskListResponseDto): TaskList => ({
    locationId: dto.locationId,
    tasks: (dto.tasks ?? []).map(mapTaskToDomain),
    summary: mapTaskSummaryToDomain(dto.summary ?? {}),
});

// ── Activity Log Mappers ────────────────────────────────────────────────────

export const mapActivityLogEntryToDomain = (dto: ActivityLogEntryDto): ActivityLogEntry => ({
    id: dto.id,
    locationId: dto.locationId,
    cageId: dto.cageId ?? null,
    cageName: dto.cageName ?? null,
    bioscopeId: dto.bioscopeId ?? null,
    taskId: dto.taskId ?? null,
    action: dto.action,
    title: dto.title ?? null,
    description: dto.description ?? null,
    alertCode: dto.alertCode ?? null,
    descriptionKey: dto.descriptionKey ?? null,
    templateVariables: toTemplateVariables(dto.templateVariables),
    comment: dto.comment ?? null,
    performedBy: dto.performedBy ?? null,
    performedByName: dto.performedByName ?? null,
    createdAt: new Date(dto.createdAt),
});

export const mapActivityLogPaginationToDomain = (
    dto: Partial<ActivityLogResponseDto['pagination']>
): ActivityLogPagination => ({
    page: dto.page ?? 1,
    limit: dto.limit ?? 20,
    total: dto.total ?? 0,
    hasMore: dto.hasMore ?? false,
});

export const mapActivityLogToDomain = (dto: ActivityLogResponseDto): ActivityLog => ({
    locationId: dto.locationId,
    entries: (dto.entries ?? []).map(mapActivityLogEntryToDomain),
    pagination: mapActivityLogPaginationToDomain(dto.pagination ?? {}),
});

// ── Pen Overview Mappers ────────────────────────────────────────────────────

export const mapPenOverviewItemToDomain = (dto: PenOverviewItemDto): PenOverviewItem => ({
    cageId: dto.cageId,
    cageName: dto.cageName,
    bioscopeId: dto.bioscopeId ?? null,
    bioscopeStatus: oneOf<BioscopeStatus>(BIOSCOPE_STATUSES, dto.bioscopeStatus),
    weightMeasurementCount: dto.weightMeasurementCount ?? null,
    weightMeasurementCountAsOf: toDateOrNull(dto.weightMeasurementCountAsOf),
    liceMeasurementCount: dto.liceMeasurementCount ?? null,
    liceMeasurementCountAsOf: toDateOrNull(dto.liceMeasurementCountAsOf),
    lastMeasurement: toDateOrNull(dto.lastMeasurement),
    activeTaskCount: dto.activeTaskCount ?? 0,
    status: oneOf<PenStatus>(PEN_STATUSES, dto.status) ?? 'offline',
});

const mapPenSummaryToDomain = (dto: PenSummaryDto): PenSummary => ({
    cageId: dto.cageId,
    cageName: dto.cageName,
});

export const mapPenOverviewToDomain = (dto: PenOverviewResponseDto): PenOverview => ({
    locationId: dto.locationId,
    locationName: dto.locationName ?? '',
    timezone: siteTimezone(dto),
    pens: (dto.pens ?? []).map(mapPenOverviewItemToDomain),
    allPens: (dto.allPens ?? []).map(mapPenSummaryToDomain),
});

/** The OpenAPI client gains `timezone` on the next regen. The payload already carries it. */
function siteTimezone(dto: PenOverviewResponseDto): string {
    const value: unknown = Reflect.get(dto, 'timezone');
    return typeof value === 'string' && value !== '' ? value : '';
}
